import { X, Stethoscope, Hospital, Pill, ArrowRight, Database } from "lucide-react";
import SectionCard from "./SectionCard";
import { formatNum } from "@/lib/constants";
import LeyendaEstado from "./pipeline/LeyendaEstado";

const ETAPA_CONFIG = {
  indicacion: {
    title: "Indicación Quirúrgica",
    subtitle: "org_indicaciones · ORDEN DE CIRUGÍA",
    icon: Stethoscope,
    color: "bg-emerald-500",
  },
  crm: {
    title: "CRM Quirúrgico",
    subtitle: "Keiron CRM v2",
    icon: Hospital,
    color: "bg-blue-500",
  },
  pabellon: {
    title: "Pabellón — Cirugías Realizadas",
    subtitle: "data_governance.hospitalario_pabellones",
    icon: Pill,
    color: "bg-amber-500",
  },
};

export default function HospitalarioDrawer({ etapa, data, periodo, onClose }) {
  if (!data || !etapa) return null;

  const cfg = ETAPA_CONFIG[etapa];
  const Icon = cfg.icon;

  // Conversiones del funnel
  const pctIndicacionCrm = data.indicaciones.total > 0
    ? ((data.crm.total / data.indicaciones.total) * 100).toFixed(1)
    : "—";
  const pctCrmCirugia = data.crm.total > 0
    ? ((data.cirugias.total / data.crm.total) * 100).toFixed(1)
    : "—";

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="hospitalario-drawer">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full max-w-lg bg-white shadow-2xl overflow-y-auto animate-in slide-in-from-right duration-300">
        {/* Header */}
        <div className="sticky top-0 z-10 p-5 border-b border-slate-200 bg-white/95 backdrop-blur">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`h-10 w-10 rounded-xl ${cfg.color} flex items-center justify-center text-white`}>
                <Icon className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">{cfg.title}</h3>
                <p className="text-xs text-slate-500">{cfg.subtitle} · {periodo}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="h-8 w-8 rounded-lg hover:bg-slate-100 flex items-center justify-center transition"
              aria-label="Cerrar panel"
            >
              <X className="w-4 h-4 text-slate-500" />
            </button>
          </div>
          <div className="mt-2 flex items-center gap-2">
            <LeyendaEstado estado={data.origen} />
            {etapa === "crm" && <LeyendaEstado estado="ESTIMADO" />}
          </div>
        </div>

        <div className="p-5 space-y-6">
          {/* KPIs según etapa */}
          {etapa === "indicacion" && (
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Indicaciones</div>
                <div className="mt-2 text-2xl font-bold text-slate-900 tabular-nums">{formatNum(data.indicaciones.total)}</div>
                <div className="text-sm text-slate-500">órdenes de cirugía</div>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Pacientes</div>
                <div className="mt-2 text-2xl font-bold text-slate-900 tabular-nums">{formatNum(data.indicaciones.pacientes)}</div>
                <div className="text-sm text-slate-500">pacientes únicos</div>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Conversión a CRM</div>
                <div className="mt-2 text-2xl font-bold text-blue-600 tabular-nums">{pctIndicacionCrm}%</div>
                <div className="text-sm text-slate-500">pasan a Keiron</div>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Conversión a cirugía</div>
                <div className="mt-2 text-2xl font-bold text-emerald-600 tabular-nums">{data.conversion_indicacion_cirugia}%</div>
                <div className="text-sm text-slate-500">se operan</div>
              </div>
            </div>
          )}

          {etapa === "crm" && (
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">En CRM</div>
                <div className="mt-2 text-2xl font-bold text-slate-900 tabular-nums">{formatNum(data.crm.total)}</div>
                <div className="text-sm text-slate-500">registros Keiron</div>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Pacientes</div>
                <div className="mt-2 text-2xl font-bold text-slate-900 tabular-nums">{formatNum(data.crm.pacientes)}</div>
                <div className="text-sm text-slate-500">pacientes únicos</div>
              </div>
              <div className="col-span-2 rounded-xl border border-blue-200 bg-blue-50 p-4">
                <div className="text-xs font-semibold text-blue-700 uppercase tracking-wider">Nota sobre Keiron</div>
                <p className="text-sm text-slate-700 mt-1">
                  Los datos de Keiron CRM v2 solo llegan hasta <strong>junio 2024</strong> en Redshift.
                  Los valores actuales son <strong>estimados al 98%</strong> de las indicaciones quirúrgicas,
                  basado en la tasa histórica de captura del CRM.
                </p>
              </div>
            </div>
          )}

          {etapa === "pabellon" && (
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Cirugías realizadas</div>
                <div className="mt-2 text-2xl font-bold text-slate-900 tabular-nums">{formatNum(data.cirugias.total)}</div>
                <div className="text-sm text-slate-500">intervenciones</div>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Pacientes</div>
                <div className="mt-2 text-2xl font-bold text-slate-900 tabular-nums">{formatNum(data.cirugias.pacientes)}</div>
                <div className="text-sm text-slate-500">pacientes únicos</div>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Conversión desde indicación</div>
                <div className="mt-2 text-2xl font-bold text-emerald-600 tabular-nums">{data.conversion_indicacion_cirugia}%</div>
                <div className="text-sm text-slate-500">indicación → cirugía</div>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Fuente</div>
                <div className="mt-2 text-sm font-medium text-slate-700">{data.cirugias.fuente}</div>
              </div>
            </div>
          )}

          {/* Conversión del funnel — siempre visible */}
          <SectionCard title="Conversión del funnel" subtitle="Indicación → CRM → Pabellón">
            <div className="flex items-center justify-between gap-2">
              {/* Indicación */}
              <div className="flex-1 text-center">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Indicación</div>
                <div className="mt-1 text-xl font-bold text-slate-900 tabular-nums">{formatNum(data.indicaciones.total)}</div>
                <div className="text-xs text-slate-500 tabular-nums">{formatNum(data.indicaciones.pacientes)} pac</div>
              </div>

              {/* Arrow + % */}
              <div className="flex flex-col items-center shrink-0">
                <span className="text-xs font-semibold text-blue-600 tabular-nums">{pctIndicacionCrm}%</span>
                <ArrowRight className="w-4 h-4 text-slate-400" />
              </div>

              {/* CRM */}
              <div className="flex-1 text-center">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">CRM</div>
                <div className="mt-1 text-xl font-bold text-slate-900 tabular-nums">{formatNum(data.crm.total)}</div>
                <div className="text-xs text-slate-500 tabular-nums">{formatNum(data.crm.pacientes)} pac</div>
              </div>

              {/* Arrow + % */}
              <div className="flex flex-col items-center shrink-0">
                <span className="text-xs font-semibold text-emerald-600 tabular-nums">{pctCrmCirugia}%</span>
                <ArrowRight className="w-4 h-4 text-slate-400" />
              </div>

              {/* Pabellón */}
              <div className="flex-1 text-center">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Pabellón</div>
                <div className="mt-1 text-xl font-bold text-slate-900 tabular-nums">{formatNum(data.cirugias.total)}</div>
                <div className="text-xs text-slate-500 tabular-nums">{formatNum(data.cirugias.pacientes)} pac</div>
              </div>
            </div>

            {/* Barra visual del funnel */}
            <div className="mt-4 space-y-1.5">
              <div className="h-3 rounded-full bg-emerald-500 w-full" title={`Indicaciones: ${formatNum(data.indicaciones.total)}`} />
              <div
                className="h-3 rounded-full bg-blue-500"
                style={{ width: `${Math.min((data.crm.total / data.indicaciones.total) * 100, 100)}%` }}
                title={`CRM: ${formatNum(data.crm.total)}`}
              />
              <div
                className="h-3 rounded-full bg-amber-500"
                style={{ width: `${Math.min((data.cirugias.total / data.indicaciones.total) * 100, 100)}%` }}
                title={`Cirugías: ${formatNum(data.cirugias.total)}`}
              />
            </div>
          </SectionCard>

          {/* Metodología */}
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 space-y-2">
            <div className="text-xs font-semibold text-blue-700 uppercase tracking-wider flex items-center gap-2">
              <Database className="w-3.5 h-3.5" /> Metodología
            </div>
            <p className="text-sm text-slate-700">
              <strong>Indicaciones:</strong> {data.indicaciones.fuente}.
              Filtro: <code className="text-xs bg-slate-100 px-1 rounded">tipo_examen = 'ORDEN DE CIRUGÍA'</code>.
            </p>
            <p className="text-sm text-slate-700">
              <strong>CRM:</strong> {data.crm.fuente}.
              Último dato real en Redshift: junio 2024. Valores actuales estimados al 98% de indicaciones.
            </p>
            <p className="text-sm text-slate-700">
              <strong>Cirugías:</strong> {data.cirugias.fuente}.
              Registros de pabellón SAP con fecha de intervención en el período.
            </p>
            <p className="text-sm text-slate-700">
              <strong>Conversión:</strong> Indicación→Cirugía = cirugías / indicaciones × 100.
              Valor {periodo}: <strong className="tabular-nums">{data.conversion_indicacion_cirugia}%</strong>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
